import type { UsageReportV2, UsageReportV2CustomFieldName, UsageReportV2ReportItem } from '@vy/types';
import ExcelJS from 'exceljs';
import {
  formatCustomFieldValue,
  formatIntervalMonths,
  formatReportDate,
  formatTierTo,
  splitPascalCase,
} from '../../../usage-report-v2-report-page/usage-report-v2-report-components/usage-report-v2-format-helpers';
import { whenDefined } from '@vy/null';

const SHEET_NAME = 'RAW_DATA';

type ExportColumn = {
  field: string;
  group: string;
  header: string;
  getValue: (item: UsageReportV2ReportItem) => string;
};

const GROUPS = Object.freeze({
  Customer: 'Customer',
  Contract: 'Contract',
  Product: 'Product',
  UsageModifications: 'Usage Modifications',
  Usage: 'Usage',
});

function buildCustomerColumns(customerCustomFieldNames: UsageReportV2CustomFieldName[]): ExportColumn[] {
  return [
    {
      field: 'customer.name',
      group: GROUPS.Customer,
      header: 'Customer Name',
      getValue: (i) => i.customer.name,
    },
    { field: 'customer.id', group: GROUPS.Customer, header: 'Customer ID', getValue: (i) => i.customer.id },
    {
      field: 'customer.externalId',
      group: GROUPS.Customer,
      header: 'External ID',
      getValue: (i) => i.customer.externalId ?? '',
    },
    ...customerCustomFieldNames.map<ExportColumn>((cf) => ({
      field: `customerCf.${cf.id}`,
      group: GROUPS.Customer,
      header: cf.name,
      getValue: (i) => formatCustomFieldValue(i.customerCustomFieldValues?.[cf.id]),
    })),
  ];
}

function buildContractColumns(contractCustomFieldNames: UsageReportV2CustomFieldName[]): ExportColumn[] {
  return [
    {
      field: 'contract.name',
      group: GROUPS.Contract,
      header: 'Contract Name',
      getValue: (i) => i.contract.name,
    },
    { field: 'contract.id', group: GROUPS.Contract, header: 'Contract ID', getValue: (i) => i.contract.id },
    {
      field: 'contract.startDate',
      group: GROUPS.Contract,
      header: 'Contract Start Date',
      getValue: (i) => formatReportDate(i.contract.startDate),
    },
    {
      field: 'contract.daysToEnd',
      group: GROUPS.Contract,
      header: 'Days to end',
      getValue: (i) => whenDefined(i.contract.daysToEnd, String, ''),
    },
    {
      field: 'contract.endDate',
      group: GROUPS.Contract,
      header: 'Contract End Date',
      getValue: (i) => (i.contract.endDate ? formatReportDate(i.contract.endDate) : ''),
    },
    {
      field: 'contract.durationMonths',
      group: GROUPS.Contract,
      header: 'Contract Duration',
      getValue: (i) => whenDefined(i.contract.durationMonths, String, 'EverGreen'),
    },
    {
      field: 'contract.status',
      group: GROUPS.Contract,
      header: 'Contract Status',
      getValue: (i) => i.contract.status,
    },
    ...contractCustomFieldNames.map<ExportColumn>((cf) => ({
      field: `contractCf.${cf.id}`,
      group: GROUPS.Contract,
      header: cf.name,
      getValue: (i) => formatCustomFieldValue(i.contractCustomFieldValues?.[cf.id]),
    })),
  ];
}

function buildProductColumns(): ExportColumn[] {
  return [
    { field: 'product.name', group: GROUPS.Product, header: 'Product Name', getValue: (i) => i.product.name },
    { field: 'product.id', group: GROUPS.Product, header: 'Product ID', getValue: (i) => i.product.id },
    {
      field: 'product.description',
      group: GROUPS.Product,
      header: 'Product Description',
      getValue: (i) => i.product.description ?? '',
    },
    {
      field: 'product.productGroupName',
      group: GROUPS.Product,
      header: 'Product Group Name',
      getValue: (i) => i.product.productGroupName ?? '',
    },
    {
      field: 'product.pricingModel',
      group: GROUPS.Product,
      header: 'Pricing Type',
      getValue: (i) => splitPascalCase(i.product.pricingModel),
    },
    {
      field: 'product.chargingCadenceMonths',
      group: GROUPS.Product,
      header: 'Period (Cadence)',
      getValue: (i) => formatIntervalMonths(i.product.chargingCadenceMonths),
    },
    {
      field: 'product.billingIntervalMonths',
      group: GROUPS.Product,
      header: 'Billing Interval',
      getValue: (i) => formatIntervalMonths(i.product.billingIntervalMonths),
    },
  ];
}

function buildUsageModificationsColumns(): ExportColumn[] {
  return [
    {
      field: 'product.commitmentUnits',
      group: GROUPS.UsageModifications,
      header: 'Commitment (Units)',
      getValue: (i) => whenDefined(i.product.commitmentUnits, String, ''),
    },
    {
      field: 'product.accumulationIntervalMonths',
      group: GROUPS.UsageModifications,
      header: 'Commitment Reset',
      getValue: (i) => formatIntervalMonths(i.product.accumulationIntervalMonths),
    },
    {
      field: 'tier.index',
      group: GROUPS.UsageModifications,
      header: 'Tier #',
      getValue: (i) => String(i.tier.index),
    },
    {
      field: 'tier.from',
      group: GROUPS.UsageModifications,
      header: 'Tiers Quantity (FROM)',
      getValue: (i) => whenDefined(i.tier.from, String, ''),
    },
    {
      field: 'tier.to',
      group: GROUPS.UsageModifications,
      header: 'Tiers Quantity (TO)',
      getValue: (i) => formatTierTo(i.tier),
    },
    {
      field: 'tier.bulkAmount',
      group: GROUPS.UsageModifications,
      header: 'Bulk Amount (Units)',
      getValue: (i) => whenDefined(i.tier.bulkAmount, String, ''),
    },
  ];
}

function buildUsageColumns(): ExportColumn[] {
  return [
    {
      field: 'period',
      group: GROUPS.Usage,
      header: 'Usage Period',
      getValue: (i) => `${formatReportDate(i.period.startTime)} - ${formatReportDate(i.period.endTime)}`,
    },
    {
      field: 'totalConsumed',
      group: GROUPS.Usage,
      header: 'Total Consumed (Contract Start - Today)',
      getValue: (i) => String(i.totalConsumed),
    },
  ];
}

function buildExportColumns(
  customerCustomFieldNames: UsageReportV2CustomFieldName[],
  contractCustomFieldNames: UsageReportV2CustomFieldName[],
): ExportColumn[] {
  return [
    ...buildCustomerColumns(customerCustomFieldNames),
    ...buildContractColumns(contractCustomFieldNames),
    ...buildProductColumns(),
    ...buildUsageModificationsColumns(),
    ...buildUsageColumns(),
  ];
}

type Group = { name: string; span: number };

function chunkGroups(columns: ExportColumn[]): Group[] {
  const groups: Group[] = [];
  for (const column of columns) {
    const last = groups[groups.length - 1];
    if (last && last.name === column.group) {
      last.span += 1;
    } else {
      groups.push({ name: column.group, span: 1 });
    }
  }
  return groups;
}

export function buildUsageReportV2Export(
  workbook: ExcelJS.Workbook,
  report: UsageReportV2,
  hiddenFields?: ReadonlySet<string>,
): void {
  const { customerCustomFieldNames, contractCustomFieldNames, reportItems } = report;
  const allColumns = buildExportColumns(customerCustomFieldNames, contractCustomFieldNames);
  const columns = hiddenFields ? allColumns.filter((column) => !hiddenFields.has(column.field)) : allColumns;
  const groups = chunkGroups(columns);

  const sheet = workbook.addWorksheet(SHEET_NAME);
  const headers = columns.map((column) => column.header);
  const groupRow = groups.flatMap((group) => [
    group.name,
    ...Array.from({ length: group.span - 1 }, () => null),
  ]);

  sheet.addRow(groupRow);
  sheet.addRow(headers);

  groups.reduce((offset, group) => {
    if (group.span > 1) {
      sheet.mergeCells(1, offset, 1, offset + group.span - 1);
    }
    return offset + group.span;
  }, 1);

  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).alignment = { horizontal: 'center' };
  sheet.getRow(2).font = { bold: true };

  for (const item of reportItems) {
    sheet.addRow(columns.map((column) => column.getValue(item)));
  }
}
